// base for the channel's screens that draw from the sidecar snapshot. A screen with nothing usable reports
// "no data", so the loop skips it instead of showing an empty box; it's checked again on each refresh.
import STATUS from '../status.mjs';
import WeatherDisplay from '../weatherdisplay.mjs';
import { ready } from './snapshot.mjs';

// screens taken out of the loop by name in config/lineup.private.json "off" (Oct 26: Headlines, since News Desk
// covers it; Oct 27: Views Around Town, since the Views blocks carry the cameras); still registered, so the nav ids stay contiguous
const OFF = new Set(window.RHL?.lineup?.off ?? []);

// rules.mjs gives a screen `airsNow(now)` (config/clock.json loopRules, Oct 27): while it says no, the screen reads as
// "no data" to the loop and the rest of the clock, so the loop passes it by. Its data status underneath is untouched,
// and a screen already on air keeps it. The control room's jump goes round the rules (byHand).
let handPicked = false;
const byHand = (fn) => {
	handPicked = true;
	try {
		return fn();
	} finally {
		handPicked = false;
	}
};

class SnapshotDisplay extends WeatherDisplay {
	constructor(navId, elemId, name, { seconds = 18 } = {}) {
		super(navId, elemId, name, true);
		this.timing.totalScreens = 1;
		// base delay is 9 s
		this.timing.delay = Math.max(1, Math.round(seconds / 9));
		// the channel's screens by id, for checking a screen's pages from the console or a headless browser
		if (window.RHL) (window.RHL.screens ??= {})[elemId] = this;
	}

	get status() {
		const status = super.status;
		if (status !== STATUS.loaded || handPicked || this.active || !this.airsNow) return status;
		return this.airsNow(new Date()) ? status : STATUS.noData;
	}

	set status(state) {
		super.status = state;
	}

	// when it last went on air, for the rules that space a screen out
	showCanvas(...args) {
		this.lastAiredAt = Date.now();
		return super.showCanvas(...args);
	}

	async getData(weatherParameters, refresh) {
		if (!super.getData(weatherParameters, refresh)) return;
		await ready();
		this.setStatus(this.isReady() ? STATUS.loaded : STATUS.noData);
	}

	// loaded means it can be shown; a display that waits for something to happen (the interrupt) stays loaded
	isReady() {
		return !OFF.has(this.elemId) && this.hasData();
	}

	// whether drawing now would have something to draw
	canRender() {
		return this.hasData();
	}

	async drawCanvas() {
		super.drawCanvas();
		const main = this.elem.querySelector('.main');
		if (this.canRender()) main.innerHTML = this.render(new Date());
		this.finishDraw();
	}

	// subclasses: is there enough to draw, and the screen's inner HTML
	// eslint-disable-next-line class-methods-use-this
	hasData() { return false; }

	// eslint-disable-next-line class-methods-use-this
	render() { return ''; }
}

export default SnapshotDisplay;
export { byHand };
